/**
 * Insight → Webhooks: every client's webhooks from one place (docs/39_WEBHOOKS.md).
 *
 * * **Settings** — the global switch (off pauses the queue: nothing is
 *   recorded or attempted, and what waits goes once it is back on), the limits
 *   every client starts from, per family, the retry policy and when a failing
 *   endpoint is switched off. A client's own switch and limits are on its page.
 * * **Deliveries** — every client's, filterable; failed is the dead-letter
 *   queue. A delivery opens with its envelope and every attempt, and can be
 *   sent again with the same event id.
 * * **Usage** — each client with endpoints, and its last 24 hours.
 */

import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { BarChart3, History, Repeat, Settings2 } from 'lucide-react';
import {
  Alert,
  ApiError,
  Button,
  Can,
  Card,
  CardBody,
  CardHeader,
  DetailField,
  DetailGrid,
  Drawer,
  Input,
  PageHeader,
  QueryPending,
  ReadOnly,
  Sections,
  Select,
  ServerTable,
  StatusChip,
  Switch,
  api,
  createClientFetcher,
  createFacetsFetcher,
  createServerFetcher,
  formatDateTime,
  formatNumber,
  toast,
  useCan,
  type ServerColumn,
} from '@connect/web-shared';

import {
  DELIVERY_TONES,
  PLATFORM,
  limitText,
  limitValue,
  switchText,
  switchValue,
  webhookKeys,
  type FamilyOverride,
  type LimitSet,
  type PlatformWebhooks,
  type StaffDelivery,
  type StaffDeliveryDetail,
  type UsageRow,
} from './webhooks';

interface CatalogFamily {
  id: string;
  label: string;
  kind: 'account' | 'channel' | 'calls';
  events: { type: string; label: string }[];
}

export function WebhooksPage() {
  return (
    <div className="space-y-4">
      <PageHeader
        title="Webhooks"
        description="Clients' endpoints, what Connect sent them, and the limits they work within."
      />
      <Sections
        label="Webhooks"
        items={[
          { id: 'settings', label: 'Settings', hint: 'Switch, limits, retries', icon: <Settings2 />, content: <SettingsCard /> },
          { id: 'deliveries', label: 'Deliveries', hint: 'Every client’s; failed ones', icon: <History />, content: <DeliveriesCard /> },
          { id: 'usage', label: 'Usage', hint: 'The last 24 hours', icon: <BarChart3 />, content: <UsageCard /> },
        ]}
      />
    </div>
  );
}

// ── settings ─────────────────────────────────────────────────
type Text<T> = { [K in keyof T]: string };

function limitsText(limits: LimitSet): Text<LimitSet> {
  return {
    max_endpoints: String(limits.max_endpoints),
    max_events_per_endpoint: limitText(limits.max_events_per_endpoint),
    max_per_minute: String(limits.max_per_minute),
  };
}

function limitsValue(form: Text<LimitSet>): LimitSet | null {
  const endpoints = limitValue(form.max_endpoints);
  const events = limitValue(form.max_events_per_endpoint);
  const minute = limitValue(form.max_per_minute);
  if (endpoints === null || minute === null || [endpoints, events, minute].some((v) => Number.isNaN(v))) return null;
  return { max_endpoints: endpoints, max_events_per_endpoint: events, max_per_minute: minute };
}

interface FamilyText {
  enabled: '' | 'on' | 'off';
  max_endpoints: string;
  max_events_per_endpoint: string;
  max_per_minute: string;
}

function SettingsCard() {
  const queryClient = useQueryClient();
  const canManage = useCan('messaging.webhook.manage');
  const settings = useQuery({ queryKey: webhookKeys.platform, queryFn: () => api.get<PlatformWebhooks>(`${PLATFORM}/settings`) });
  const catalog = useQuery({ queryKey: [...webhookKeys.all, 'catalog'], queryFn: () => api.get<CatalogFamily[]>(`${PLATFORM}/catalog`) });
  const [form, setForm] = useState<PlatformWebhooks | null>(null);
  const [account, setAccount] = useState<Text<LimitSet> | null>(null);
  const [services, setServices] = useState<Text<LimitSet> | null>(null);
  const [families, setFamilies] = useState<Record<string, FamilyText>>({});
  const [numbers, setNumbers] = useState<Record<string, string>>({});

  useEffect(() => {
    const data = settings.data;
    if (!data) return;
    setForm(data);
    setAccount(limitsText(data.account));
    setServices(limitsText(data.services));
    setFamilies(
      Object.fromEntries(
        Object.entries(data.families).map(([id, o]) => [
          id,
          {
            enabled: switchText(o.enabled),
            max_endpoints: limitText(o.max_endpoints),
            max_events_per_endpoint: limitText(o.max_events_per_endpoint),
            max_per_minute: limitText(o.max_per_minute),
          },
        ]),
      ),
    );
    setNumbers({
      max_endpoints_per_client: String(data.max_endpoints_per_client),
      max_attempts: String(data.retry.max_attempts),
      base_seconds: String(data.retry.base_seconds),
      factor: String(data.retry.factor),
      max_backoff_seconds: String(data.retry.max_backoff_seconds),
      timeout_seconds: String(data.retry.timeout_seconds),
      disable_after_failures: String(data.disable_after_failures),
      disable_after_hours: String(data.disable_after_hours),
      retention_days: String(data.retention_days),
    });
  }, [settings.data]);

  const body = useMemo(() => {
    if (!form || !account || !services) return null;
    const accountLimits = limitsValue(account);
    const serviceLimits = limitsValue(services);
    const n = (key: string) => Number(numbers[key]);
    if (!accountLimits || !serviceLimits || Object.values(numbers).some((v) => !v.trim() || Number.isNaN(Number(v)))) return null;
    const overrides: Record<string, Partial<FamilyOverride>> = {};
    for (const [id, f] of Object.entries(families)) {
      const entry: Partial<FamilyOverride> = {};
      const enabled = switchValue(f.enabled);
      if (enabled !== null) entry.enabled = enabled;
      for (const key of ['max_endpoints', 'max_events_per_endpoint', 'max_per_minute'] as const) {
        const value = limitValue(f[key]);
        if (Number.isNaN(value)) return null;
        if (value !== null) entry[key] = value;
      }
      if (Object.keys(entry).length) overrides[id] = entry;
    }
    return {
      ...form,
      max_endpoints_per_client: n('max_endpoints_per_client'),
      account: accountLimits,
      services: serviceLimits,
      families: overrides,
      retry: {
        max_attempts: n('max_attempts'),
        base_seconds: n('base_seconds'),
        factor: n('factor'),
        max_backoff_seconds: n('max_backoff_seconds'),
        timeout_seconds: n('timeout_seconds'),
      },
      disable_after_failures: n('disable_after_failures'),
      disable_after_hours: n('disable_after_hours'),
      retention_days: n('retention_days'),
    } satisfies PlatformWebhooks;
  }, [form, account, services, families, numbers]);

  const save = useMutation({
    mutationFn: () => api.put<PlatformWebhooks>(`${PLATFORM}/settings`, body),
    onSuccess: (saved) => {
      queryClient.setQueryData(webhookKeys.platform, saved);
      void queryClient.invalidateQueries({ queryKey: webhookKeys.all });
      toast.success('Saved', saved.enabled ? 'Webhooks are on.' : 'Webhooks are off for every client.');
    },
    onError: (caught) => toast.error('Could not save', caught instanceof ApiError ? caught.message : undefined),
  });

  if (form === null || account === null || services === null || catalog.data === undefined) {
    return <QueryPending query={settings.data === undefined ? settings : catalog} label="Loading the settings" />;
  }
  const number = (key: string, label: string, hint?: string) => (
    <Input
      label={label}
      hint={hint}
      inputMode="decimal"
      value={numbers[key] ?? ''}
      onChange={(event) => setNumbers((current) => ({ ...current, [key]: event.target.value }))}
    />
  );
  const limitFields = (value: Text<LimitSet>, set: (next: Text<LimitSet>) => void) => (
    <div className="grid gap-3 sm:grid-cols-3">
      <Input label="Endpoints" inputMode="numeric" value={value.max_endpoints} onChange={(e) => set({ ...value, max_endpoints: e.target.value })} />
      <Input
        label="Events each"
        inputMode="numeric"
        placeholder="All"
        value={value.max_events_per_endpoint}
        hint="Blank: every event of the family."
        onChange={(e) => set({ ...value, max_events_per_endpoint: e.target.value })}
      />
      <Input label="Attempts per minute, each" inputMode="numeric" value={value.max_per_minute} onChange={(e) => set({ ...value, max_per_minute: e.target.value })} />
    </div>
  );
  const familyField = (id: string, key: keyof FamilyText, value: string) =>
    setFamilies((current) => ({
      ...current,
      [id]: { enabled: '', max_endpoints: '', max_events_per_endpoint: '', max_per_minute: '', ...current[id], [key]: value },
    }));

  return (
    <ReadOnly when={!canManage}>
      <div className="space-y-4">
        <Card>
          <CardHeader title="Webhooks on this installation" />
          <CardBody className="space-y-4">
            <Switch
              label="Webhooks on"
              checked={form.enabled}
              hint={
                form.enabled
                  ? 'Clients switched on, one by one on their own page, have webhooks.'
                  : 'Off: no client has webhooks, nothing is recorded, and the queue waits — what was pending goes once it is back on.'
              }
              onChange={(event) => setForm({ ...form, enabled: event.target.checked })}
            />
            <div className="grid gap-3 sm:grid-cols-3">
              {number('max_endpoints_per_client', 'Endpoints per client, in all')}
              {number('retention_days', 'Keep the log for (days)')}
            </div>
          </CardBody>
        </Card>

        <Card>
          <CardHeader
            title="Limits every client starts from"
            description="A client's own page can set others for it. Account events go to the account's endpoints; each service's to its own."
          />
          <CardBody className="space-y-5">
            <div className="space-y-2">
              <h3 className="text-sm font-medium text-heading">Account</h3>
              {limitFields(account, setAccount)}
            </div>
            <div className="space-y-2">
              <h3 className="text-sm font-medium text-heading">Each service</h3>
              {limitFields(services, setServices)}
            </div>
            <div className="space-y-2">
              <h3 className="text-sm font-medium text-heading">By family — blank follows the above</h3>
              <ul className="divide-y divide-line rounded-control border border-line">
                {catalog.data.map((family) => {
                  const f = families[family.id] ?? { enabled: '', max_endpoints: '', max_events_per_endpoint: '', max_per_minute: '' };
                  return (
                    <li key={family.id} className="grid gap-3 px-3 py-3 sm:grid-cols-[minmax(0,1fr)_repeat(4,minmax(0,8rem))]">
                      <div className="min-w-0 text-sm">
                        <p className="font-medium text-heading">{family.kind === 'account' ? 'Account' : family.label}</p>
                        <p className="text-xs text-muted">{family.events.map((e) => e.type).join(', ')}</p>
                      </div>
                      <Select
                        label="Switch"
                        size="sm"
                        value={f.enabled}
                        options={[
                          { value: '', label: 'On' },
                          { value: 'off', label: 'Off for everyone' },
                        ]}
                        onChange={(event) => familyField(family.id, 'enabled', event.target.value)}
                      />
                      <Input label="Endpoints" size="sm" inputMode="numeric" value={f.max_endpoints} onChange={(e) => familyField(family.id, 'max_endpoints', e.target.value)} />
                      <Input label="Events each" size="sm" inputMode="numeric" value={f.max_events_per_endpoint} onChange={(e) => familyField(family.id, 'max_events_per_endpoint', e.target.value)} />
                      <Input label="Per minute" size="sm" inputMode="numeric" value={f.max_per_minute} onChange={(e) => familyField(family.id, 'max_per_minute', e.target.value)} />
                    </li>
                  );
                })}
              </ul>
            </div>
          </CardBody>
        </Card>

        <Card>
          <CardHeader
            title="Retries and failing endpoints"
            description="A call that fails is tried again after base × factorⁿ, capped and spread by a tenth either way. After the last attempt it is marked failed and can be sent again."
          />
          <CardBody className="space-y-4">
            <div className="grid gap-3 sm:grid-cols-3">
              {number('max_attempts', 'Attempts in all')}
              {number('base_seconds', 'First wait (seconds)')}
              {number('factor', 'Each wait longer by')}
              {number('max_backoff_seconds', 'Longest wait (seconds)')}
              {number('timeout_seconds', 'Wait for an answer (seconds)', '30 at most.')}
            </div>
            <div className="grid gap-3 sm:grid-cols-3">
              {number('disable_after_failures', 'Switch off after failures in a row')}
              {number('disable_after_hours', '…failing for at least (hours)')}
            </div>
          </CardBody>
        </Card>

        {body === null ? <Alert tone="danger" title="Each field is a number" /> : null}
        <Can permission="messaging.webhook.manage">
          <Button loading={save.isPending} disabled={body === null} onClick={() => save.mutate()}>
            Save settings
          </Button>
        </Can>
      </div>
    </ReadOnly>
  );
}

// ── deliveries ───────────────────────────────────────────────
const STATUS_LABELS: Record<StaffDelivery['status'], string> = {
  pending: 'Waiting',
  sending: 'Sending',
  delivered: 'Delivered',
  failed: 'Failed',
  cancelled: 'Not sent',
};

function DeliveriesCard() {
  const [open, setOpen] = useState<StaffDelivery | null>(null);
  // Labels only: a family the catalog no longer declares still reads as its id.
  const catalog = useQuery({ queryKey: [...webhookKeys.all, 'catalog'], queryFn: () => api.get<CatalogFamily[]>(`${PLATFORM}/catalog`) });
  const labels = useMemo(
    () => new Map((catalog.data ?? []).map((family) => [family.id, family.kind === 'account' ? 'Account' : family.label])),
    [catalog.data],
  );
  const fetchRows = useMemo(() => createServerFetcher<StaffDelivery>(`${PLATFORM}/deliveries`), []);
  const getFacets = useMemo(() => createFacetsFetcher(`${PLATFORM}/deliveries`), []);
  const columns = useMemo<ServerColumn<StaffDelivery>[]>(
    () => [
      { field: 'created_at', header: 'When', width: 170, render: (row) => formatDateTime(row.created_at) },
      {
        field: 'client_name',
        header: 'Client',
        minWidth: 180,
        render: (row) =>
          row.client_public_id ? (
            <Link className="text-accent hover:underline" to={`/clients/${row.client_public_id}?tab=services&area=webhooks`}>
              {row.client_name}
            </Link>
          ) : (
            row.client_name
          ),
      },
      { field: 'event', header: 'Event', minWidth: 160, filter: 'select', render: (row) => <span className="font-mono text-xs">{row.event}</span> },
      { field: 'family', header: 'Family', width: 120, filter: 'select', render: (row) => labels.get(row.family) ?? row.family },
      {
        field: 'status',
        header: 'Status',
        width: 130,
        filter: 'select',
        render: (row) => <StatusChip value={row.status} tones={DELIVERY_TONES} labels={STATUS_LABELS} />,
      },
      { field: 'attempts', header: 'Attempts', width: 100 },
      { field: 'response_status', header: 'Answer', width: 100, render: (row) => (row.response_status ? `HTTP ${row.response_status}` : '—') },
      { field: 'url', header: 'Endpoint', minWidth: 220, render: (row) => <span className="break-all font-mono text-xs">{row.url}</span> },
    ],
    [labels],
  );
  return (
    <>
      <ServerTable<StaffDelivery>
        columns={columns}
        fetchRows={fetchRows}
        getFacets={getFacets}
        rowKey={(row) => row.public_id}
        queryKey={webhookKeys.deliveries}
        stateKey="webhooks.deliveries"
        searchPlaceholder="Search by client, event or address"
        onView={setOpen}
        emptyTitle="No deliveries"
        emptyDescription="Every call Connect makes to a client's endpoint is listed here for as long as the log is kept."
      />
      {open ? <DeliveryDrawer row={open} onClose={() => setOpen(null)} /> : null}
    </>
  );
}

function DeliveryDrawer({ row, onClose }: { row: StaffDelivery; onClose: () => void }) {
  const queryClient = useQueryClient();
  const detail = useQuery({
    queryKey: webhookKeys.delivery(row.public_id),
    queryFn: () => api.get<StaffDeliveryDetail>(`${PLATFORM}/deliveries/${row.public_id}`),
  });
  const again = useMutation({
    mutationFn: () => api.post<StaffDeliveryDetail>(`${PLATFORM}/deliveries/${row.public_id}/redeliver`),
    onSuccess: (data) => {
      queryClient.setQueryData(webhookKeys.delivery(row.public_id), data);
      void queryClient.invalidateQueries({ queryKey: webhookKeys.deliveries });
      toast.success('Sending it again', 'With the same event id.');
    },
    onError: (caught) => toast.error('Could not send it again', caught instanceof ApiError ? caught.message : undefined),
  });
  const data = detail.data;
  const finished = data !== undefined && ['delivered', 'failed', 'cancelled'].includes(data.status);
  return (
    <Drawer
      open
      onClose={onClose}
      width="lg"
      title={<span className="font-mono">{row.event}</span>}
      footer={
        finished ? (
          <Can permission="messaging.webhook.edit">
            <Button loading={again.isPending} onClick={() => again.mutate()}>
              <Repeat className="size-3.5" />
              Send again
            </Button>
          </Can>
        ) : null
      }
    >
      {data === undefined ? (
        <QueryPending query={detail} label="Loading the delivery" />
      ) : (
        <div className="space-y-5">
          <DetailGrid>
            <DetailField label="Client">{data.client_name}</DetailField>
            <DetailField label="Status">
              <StatusChip value={data.status} tones={DELIVERY_TONES} labels={STATUS_LABELS} />
            </DetailField>
            <DetailField label="Attempts">{data.attempts}</DetailField>
            <DetailField label="Sent again">{data.redeliveries}</DetailField>
            <DetailField label="Event id" className="col-span-2">
              <span className="break-all font-mono text-xs">{data.event_id}</span>
            </DetailField>
          </DetailGrid>
          {data.last_error ? <Alert tone="danger" title="Last error">{data.last_error}</Alert> : null}
          <section className="space-y-2">
            <h3 className="text-sm font-medium text-heading">Attempts</h3>
            <ul className="divide-y divide-line rounded-control border border-line text-xs">
              {data.attempts_log.map((attempt) => (
                <li key={`${attempt.number}-${attempt.at}`} className="space-y-1 px-3 py-2">
                  <div className="flex flex-wrap justify-between gap-2">
                    <span>
                      Attempt {attempt.number} · {formatDateTime(attempt.at)}
                    </span>
                    <span className="text-muted">
                      {attempt.response_status ? `HTTP ${attempt.response_status}` : 'No answer'}
                      {attempt.duration_ms !== null ? ` · ${attempt.duration_ms} ms` : ''}
                    </span>
                  </div>
                  {attempt.error ? <p className="text-danger">{attempt.error}</p> : null}
                  {attempt.response_body ? <p className="break-all font-mono text-muted">{attempt.response_body}</p> : null}
                </li>
              ))}
            </ul>
          </section>
          <section className="space-y-2">
            <h3 className="text-sm font-medium text-heading">Envelope</h3>
            <pre className="whitespace-pre-wrap break-all rounded-control bg-surface-muted p-3 font-mono text-xs">
              {JSON.stringify(data.payload, null, 2)}
            </pre>
          </section>
        </div>
      )}
    </Drawer>
  );
}

// ── usage ────────────────────────────────────────────────────
function UsageCard() {
  const usage = useQuery({ queryKey: webhookKeys.usage, queryFn: () => api.get<UsageRow[]>(`${PLATFORM}/usage`) });
  const columns = useMemo<ServerColumn<UsageRow & Record<string, unknown>>[]>(
    () => [
      {
        field: 'client_name',
        header: 'Client',
        minWidth: 200,
        render: (row) => (
          <Link className="text-accent hover:underline" to={`/clients/${row.client_public_id}?tab=services&area=webhooks`}>
            {row.client_name}
          </Link>
        ),
      },
      { field: 'endpoints', header: 'Endpoints', width: 110 },
      { field: 'delivered', header: 'Delivered', width: 110, render: (row) => formatNumber(row.delivered) },
      { field: 'failed', header: 'Failed', width: 100, render: (row) => formatNumber(row.failed) },
      { field: 'pending', header: 'Waiting', width: 100, render: (row) => formatNumber(row.pending) },
      { field: 'cancelled', header: 'Not sent', width: 100, render: (row) => formatNumber(row.cancelled) },
    ],
    [],
  );
  const rows = usage.data;
  const fetchRows = useMemo(
    () => createClientFetcher<UsageRow & Record<string, unknown>>((rows ?? []) as (UsageRow & Record<string, unknown>)[], ['client_name']),
    [rows],
  );
  if (rows === undefined) return <QueryPending query={usage} label="Loading usage" />;
  return (
    <ServerTable<UsageRow & Record<string, unknown>>
      columns={columns}
      fetchRows={fetchRows}
      rowKey={(row) => row.client_public_id}
      queryKey={[...webhookKeys.usage, rows.length]}
      stateKey="webhooks.usage"
      searchPlaceholder="Search by client"
      emptyTitle="No client has an endpoint"
      emptyDescription="Switch webhooks on for a client on its page; its endpoints appear here."
    />
  );
}
